// app.js — Main orchestrator for Peerly
// Wires together all modules, binds events, manages the call lifecycle.

import { FIREBASE_CONFIG, ANALYTICS, FONT_SIZES } from './config.js';
import {
  getState,
  getMutableState,
  update,
  reset,
  Phase,
  Role,
  generateSessionId,
  normalizeSlug,
  parseJoinInput,
} from './state.js';
import * as signaling from './signaling.js';
import * as rtc from './rtc.js';
import * as chat from './chat.js';
import * as ui from './ui.js';
import {
  isMobileDevice,
  isTouchDevice,
  showNotification,
  copyToClipboard,
  buildSessionUrl,
  captureFrame,
  el,
} from './utils.js';
import { setIcon } from './icons.js';

/**
 * Boot the application.
 */
export async function boot() {
  // Initialize Firebase
  firebase.initializeApp(FIREBASE_CONFIG);

  // Initialize signaling with Firestore
  signaling.init(firebase.firestore());

  // Initialize UI — caches DOM refs
  ui.init();

  // Initialize chat with DOM refs
  const DOM = ui.getDOM();
  chat.init({
    chatMessages: document.getElementById('chatMessages'),
    chatInput: DOM.chatInput,
    emojiPickerContent: document.getElementById('emojiPickerContent'),
    emojiPickerPagination: document.getElementById('emojiPickerPagination'),
  });

  // Set up RTC callbacks
  rtc.setCallbacks({
    onRemoteTrack: handleRemoteTrack,
    onDataMessage: handleDataMessage,
    onConnectionStateChange: handleConnectionStateChange,
    onDataChannelStateChange: handleDataChannelStateChange,
    onQualityUpdate: handleQualityUpdate,
  });

  // Set up file transfer callbacks
  rtc.setFileCallbacks({
    onFileProgress: (transfer) => ui.renderFileProgress(transfer),
    onFileComplete: ({ name }) => showNotification(`File received: ${name}`, 'success'),
    onFileRequest: (fileInfo) =>
      ui.renderFileRequest(
        fileInfo,
        () => rtc.respondFileRequest(fileInfo.id, true),
        () => rtc.respondFileRequest(fileInfo.id, false),
      ),
  });

  // Set local video ref for screen share
  const { localVideo } = ui.getVideoElements();
  if (localVideo) rtc.setLocalVideoRef(localVideo);

  // Parse URL to determine role and room
  const urlParams = new URLSearchParams(window.location.search);
  const sessionParam = urlParams.get('session');
  let roomId;
  let role;

  if (sessionParam) {
    const slugResult = normalizeSlug(sessionParam);
    if (slugResult.valid) {
      roomId = slugResult.slug;
      role = Role.RECEIVER;
    } else {
      // Invalid session param — fall back to sender with fresh room
      roomId = generateSessionId();
      role = Role.SENDER;
    }
  } else {
    roomId = generateSessionId();
    role = Role.SENDER;
  }

  update({ roomId, role });

  // Touch device class
  if (isTouchDevice()) {
    document.body.classList.add('touch-device');
  }

  // Mobile adjustments
  if (isMobileDevice()) {
    const splitBtn = document.getElementById('splitViewBtn');
    if (splitBtn) splitBtn.classList.add('hidden');
  }

  // Subscribe UI to state changes
  ui.bindStateRendering();

  // Configure connection panel for role
  ui.configureConnectionPanel(role, buildSessionUrl(roomId));

  // Bind all event handlers
  bindEvents();

  // Render emoji picker
  chat.renderEmojiPicker();

  // Start device preview
  startDevicePreview();

  // Auto-rejoin if refreshing during an active call
  if (role === Role.RECEIVER && roomId) {
    try {
      const room = await signaling.checkRoom(roomId);
      if (room.exists && !room.isStale && room.status === 'active') {
        startCall();
        return;
      }
    } catch (err) {
      console.warn('[app] Auto-rejoin check failed:', err);
    }
  }

  // Load analytics if enabled
  if (ANALYTICS.enabled) {
    loadAnalytics();
  }
}

/**
 * Bind all UI event handlers.
 */
function bindEvents() {
  const DOM = ui.getDOM();

  // --- Start Call ---
  DOM.startCallBtn?.addEventListener('click', startCall);

  // --- Enter key on connection panel inputs starts call ---
  DOM.nicknameInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') startCall();
  });
  DOM.customLinkInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') startCall();
  });

  // --- Join Code ---
  DOM.joinCodeBtn?.addEventListener('click', joinByCode);
  DOM.joinCodeInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') joinByCode();
  });

  // --- Device preview camera switch ---
  const previewCamera = document.getElementById('previewCamera');
  previewCamera?.addEventListener('change', async () => {
    const stream = await rtc.switchPreviewCamera(previewCamera.value);
    const previewVideo = document.getElementById('previewVideo');
    if (stream && previewVideo) {
      previewVideo.srcObject = stream;
    }
  });

  // --- End Call ---
  DOM.endCallBtn?.addEventListener('click', endCall);

  // --- Share Link ---
  DOM.shareLinkBtn?.addEventListener('click', async () => {
    const s = getState();
    const url = s.roomId ? buildSessionUrl(s.roomId) : window.location.href;
    const ok = await copyToClipboard(url);
    if (ok) {
      setIcon(DOM.shareLinkBtn, 'check');
      setTimeout(() => {
        setIcon(DOM.shareLinkBtn, 'link');
      }, 2000);
      showNotification('Room link copied!', 'success');
    } else {
      showNotification('Failed to copy link', 'error');
    }
  });

  // --- Copy Link (on panel) ---
  DOM.copyLinkBtn?.addEventListener('click', async () => {
    const s = getState();
    const customSlug = normalizeSlugFromInput();
    const linkId = customSlug || s.roomId;
    const url = buildSessionUrl(linkId);
    const ok = await copyToClipboard(url);
    if (ok) {
      const labelSpan = DOM.copyLinkBtn.querySelector('span');
      const originalLabel = labelSpan ? labelSpan.textContent : '';
      setIcon(DOM.copyLinkBtn, 'check');
      if (labelSpan) labelSpan.textContent = 'Copied!';
      setTimeout(() => {
        setIcon(DOM.copyLinkBtn, 'clipboard');
        if (labelSpan) labelSpan.textContent = originalLabel || 'Copy Link';
      }, 2000);
      showNotification('Link copied to clipboard!', 'success');
    } else {
      showNotification('Failed to copy', 'error');
    }
  });

  // --- Custom link input ---
  let _linkInputRaf = null;
  DOM.customLinkInput?.addEventListener('input', () => {
    if (_linkInputRaf) cancelAnimationFrame(_linkInputRaf);
    _linkInputRaf = requestAnimationFrame(() => {
      const slug = normalizeSlugFromInput();
      ui.updateShareLinkDisplay(slug, getMutableState().roomId);
    });
  });

  // --- Toggle Video ---
  DOM.toggleVideoBtn?.addEventListener('click', () => {
    const s = getMutableState();
    const newVal = !s.isVideoEnabled;
    update({ isVideoEnabled: newVal });
    if (s.localStream) {
      const vt = s.localStream.getVideoTracks()[0];
      if (vt) vt.enabled = newVal;
    }
  });

  // --- Toggle Audio ---
  DOM.toggleAudioBtn?.addEventListener('click', () => {
    const s = getMutableState();
    const newVal = !s.isAudioEnabled;
    update({ isAudioEnabled: newVal });
    if (s.localStream) {
      const at = s.localStream.getAudioTracks()[0];
      if (at) at.enabled = newVal;
    }
  });

  // --- Mirror Camera ---
  DOM.mirrorCamBtn?.addEventListener('click', () => {
    update({ isMirrored: !getMutableState().isMirrored });
  });

  // --- Cool Mode ---
  DOM.coolModeToggle?.addEventListener('click', () => {
    const newVal = !getMutableState().isCoolMode;
    update({ isCoolMode: newVal });
    showNotification(newVal ? 'Cool Mode activated!' : 'Cool Mode deactivated', 'info');
  });

  // --- Theme Toggle ---
  DOM.themeToggle?.addEventListener('click', () => {
    update({ isDarkMode: !getMutableState().isDarkMode });
  });

  // --- Snapshots ---
  DOM.snapshotBtn?.addEventListener('click', () => {
    const { localVideo } = ui.getVideoElements();
    captureFrame(localVideo, 'local');
  });
  DOM.remoteCaptureBtn?.addEventListener('click', () => {
    const { remoteVideo } = ui.getVideoElements();
    captureFrame(remoteVideo, 'remote');
  });

  // --- Device switching ---
  DOM.cameraSwitch?.addEventListener('change', (e) => {
    const audioId = DOM.audioSwitch?.value || null;
    switchDevices(e.target.value, audioId);
  });
  DOM.audioSwitch?.addEventListener('change', (e) => {
    const videoId = DOM.cameraSwitch?.value || null;
    switchDevices(videoId, e.target.value);
  });

  // --- Remote volume ---
  DOM.remoteVolumeSlider?.addEventListener('input', (e) => {
    const { remoteVideo } = ui.getVideoElements();
    if (remoteVideo) remoteVideo.volume = e.target.value / 100;
  });

  // --- Fullscreen ---
  DOM.fullscreenBtn?.addEventListener('click', () => {
    update({ isFullscreen: !getMutableState().isFullscreen });
  });

  // --- Split View ---
  DOM.splitViewBtn?.addEventListener('click', () => {
    if (isMobileDevice()) return;
    update({ isSplitView: !getMutableState().isSplitView });
  });

  // --- Swap Sides ---
  DOM.swapSidesBtn?.addEventListener('click', () => {
    update({ isSwapped: !getMutableState().isSwapped });
  });

  // --- Chat Send ---
  DOM.sendMessageBtn?.addEventListener('click', () => chat.sendMessage());
  DOM.chatInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') chat.sendMessage();
  });

  // --- Emoji Picker ---
  DOM.emojiBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    update({ isEmojiPickerOpen: !getMutableState().isEmojiPickerOpen });
  });
  document.addEventListener('click', (e) => {
    if (!getMutableState().isEmojiPickerOpen) return;
    if (DOM.emojiPicker && !DOM.emojiPicker.contains(e.target)) {
      update({ isEmojiPickerOpen: false });
    }
  });

  // --- Font Size ---
  DOM.fontSizeBtn?.addEventListener('click', () => {
    const s = getMutableState();
    const next = (s.currentFontSizeIndex + 1) % FONT_SIZES.length;
    update({ currentFontSizeIndex: next });
    localStorage.setItem('chatFontSize', next);
  });

  // --- QR Share ---
  DOM.qrShareBtn?.addEventListener('click', () => ui.showQRModal());
  DOM.qrDownloadBtn?.addEventListener('click', () => {
    const canvas = DOM.panelQrCanvas;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `peerly-qr-${Date.now()}.png`;
    a.click();
    showNotification('QR code downloaded!', 'success');
  });

  // --- Screen Share ---
  DOM.screenShareBtn?.addEventListener('click', async () => {
    const s = getState();
    if (s.isScreenSharing) {
      await rtc.stopScreenShare();
    } else {
      const ok = await rtc.startScreenShare();
      if (ok) showNotification('Screen sharing started', 'info');
    }
  });

  // --- File Transfer ---
  DOM.fileTransferBtn?.addEventListener('click', () => {
    DOM.fileInput?.click();
  });
  DOM.fileInput?.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await rtc.sendFile(file);
    }
    e.target.value = '';
  });

  // --- Picture-in-Picture ---
  DOM.pipBtn?.addEventListener('click', async () => {
    const { remoteVideo } = ui.getVideoElements();
    if (!remoteVideo) return;
    const s = getState();
    if (s.isPiPActive) {
      try {
        await document.exitPictureInPicture();
      } catch {}
    } else {
      try {
        await remoteVideo.requestPictureInPicture();
      } catch {
        showNotification('PiP not supported in this browser', 'error');
      }
    }
  });

  // Listen for PiP exit (user can close PiP window directly)
  document.addEventListener('leavepictureinpicture', () => {
    update({ isPiPActive: false });
  });
  document.addEventListener('enterpictureinpicture', () => {
    update({ isPiPActive: true });
  });

  // --- Keyboard Shortcuts ---
  document.addEventListener('keydown', handleKeyboardShortcut);

  // --- Before Unload ---
  window.addEventListener('beforeunload', handleBeforeUnload);
}

/**
 * Toggle the Start Call button loading state.
 */
function setStartCallLoading(loading) {
  const DOM = ui.getDOM();
  if (!DOM.startCallBtn) return;
  if (loading) {
    DOM.startCallBtn.disabled = true;
    DOM.startCallBtn.dataset.originalText = DOM.startCallBtn.textContent;
    DOM.startCallBtn.textContent = 'Connecting…';
  } else {
    DOM.startCallBtn.disabled = false;
    if (DOM.startCallBtn.dataset.originalText) {
      DOM.startCallBtn.textContent = DOM.startCallBtn.dataset.originalText;
    }
  }
}

/**
 * Start the pre-call device preview.
 * Requests camera access and populates device selectors.
 * Fails gracefully if permission denied — preview hidden, call still works.
 */
async function startDevicePreview() {
  const previewVideo = document.getElementById('previewVideo');
  const previewCamera = document.getElementById('previewCamera');
  const previewMic = document.getElementById('previewMic');

  // Always try to enumerate devices, even if preview stream fails
  let videoDevices = [];
  let audioDevices = [];
  let stream = null;

  const result = await rtc.startPreview();
  if (result) {
    stream = result.stream;
    videoDevices = result.videoDevices;
    audioDevices = result.audioDevices;
  } else {
    // Permission denied or no camera — hide just the video, keep dropdowns
    if (previewVideo) previewVideo.style.display = 'none';
    // Try to enumerate devices without stream (labels may be empty)
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      videoDevices = devices.filter((d) => d.kind === 'videoinput');
      audioDevices = devices.filter((d) => d.kind === 'audioinput');
    } catch {
      // Enumeration also failed — selects will show 'No device found'
    }
  }

  // Attach preview stream to video element
  if (stream && previewVideo) {
    previewVideo.srcObject = stream;
  }

  // Populate camera selector
  if (previewCamera) {
    previewCamera.innerHTML = '';
    if (videoDevices.length === 0) {
      previewCamera.appendChild(
        el('option', { value: '', disabled: true, textContent: 'No camera found' }),
      );
    }
    videoDevices.forEach((device, i) => {
      const option = el('option', {
        value: device.deviceId,
        textContent: device.label || `Camera ${i + 1}`,
      });
      previewCamera.appendChild(option);
    });
    if (videoDevices.length > 0) previewCamera.selectedIndex = 0;
  }

  // Populate mic selector
  if (previewMic) {
    previewMic.innerHTML = '';
    if (audioDevices.length === 0) {
      previewMic.appendChild(
        el('option', { value: '', disabled: true, textContent: 'No microphone found' }),
      );
    }
    audioDevices.forEach((device, i) => {
      const option = el('option', {
        value: device.deviceId,
        textContent: device.label || `Microphone ${i + 1}`,
      });
      previewMic.appendChild(option);
    });
    if (audioDevices.length > 0) previewMic.selectedIndex = 0;
  }
}

/**
 * Start a call (sender or receiver).
 */
async function startCall() {
  const DOM = ui.getDOM();

  // Clear any panel error state from previous attempt
  ui.clearPanelError();

  // Resolve custom slug or keep auto-generated
  const customSlug = normalizeSlugFromInput();
  if (customSlug) {
    // Validate slug
    const result = normalizeSlug(customSlug);
    if (!result.valid) {
      showNotification(result.error, 'error');
      return;
    }
    update({ roomId: result.slug, role: Role.SENDER });
  }

  // Re-read state after potential slug update to avoid stale snapshot
  const state = getState();
  const roomId = state.roomId;

  // Get nickname
  const nick = DOM.nicknameInput?.value?.trim()?.slice(0, 30) || 'Anonymous';
  update({ localNickname: nick });

  // Update URL
  const newUrl = `${window.location.pathname}?session=${encodeURIComponent(roomId)}`;
  window.history.pushState({ path: newUrl }, '', newUrl);

  // Transition to preparing media — show loading state on button
  update({ phase: Phase.PREPARING_MEDIA });
  setStartCallLoading(true);

  // Stop the preview stream before initializing call media
  rtc.stopPreview();

  // Get selected devices from preview selectors
  const previewCamera = document.getElementById('previewCamera');
  const previewMic = document.getElementById('previewMic');
  const videoDeviceId = previewCamera?.value || null;
  const audioDeviceId = previewMic?.value || null;

  // Initialize media with selected devices
  const stream = await rtc.initializeMedia(videoDeviceId, audioDeviceId);
  if (!stream) {
    update({ phase: Phase.FAILED, lastError: 'Could not access camera/microphone' });
    setStartCallLoading(false);
    return;
  }

  // Populate device lists
  const { videoDevices, audioDevices } = await rtc.getDevices();
  if (videoDevices.length > 0 || audioDevices.length > 0) {
    ui.populateDeviceSelects(videoDevices, audioDevices);
  }

  // Set up audio visualizer
  const vizLevel = document.getElementById('audioVisualizerLevel');
  if (vizLevel) rtc.setupAudioVisualizer(vizLevel);

  // Create peer connection
  try {
    await rtc.createPeerConnection();
  } catch (err) {
    console.error('[app] Failed to create peer connection:', err);
    update({ phase: Phase.FAILED, lastError: 'Could not establish peer connection' });
    setStartCallLoading(false);
    showNotification('Failed to establish connection', 'error');
    return;
  }

  // Start the appropriate flow
  if (state.role === Role.SENDER) {
    const result = await rtc.startSenderFlow(roomId);
    if (!result.success) {
      if (result.error === 'room_active') {
        const ok = await ui.showConfirmModal({
          title: 'Room In Use',
          message: 'A room with that name is active. Start a new session anyway?',
          confirmLabel: 'New Session',
          cancelLabel: 'Cancel',
        });
        if (!ok) {
          showNotification('Room appears active — try a different name', 'error');
          await rtc.teardown();
          reset();
          update({ roomId: generateSessionId() });
          ui.configureConnectionPanel(Role.SENDER, buildSessionUrl(getState().roomId));
          setStartCallLoading(false);
          return;
        }
        // Force overwrite: clean stale room via signaling layer and retry
        await signaling.forceOverwriteRoom(roomId);
        // Retry
        const retryResult = await rtc.startSenderFlow(roomId);
        if (!retryResult.success) {
          showNotification('Could not create room', 'error');
          await rtc.teardown();
          reset();
          setStartCallLoading(false);
          return;
        }
      } else {
        showNotification(`Could not create room: ${result.error}`, 'error');
        await rtc.teardown();
        reset();
        setStartCallLoading(false);
        return;
      }
    }
    chat.addCommandMessage('⏳ Waiting for your peer…');
  } else {
    const result = await rtc.startReceiverFlow(roomId);
    if (!result.success) {
      const errorModes = {
        room_not_found: 'roomNotFound',
        room_ended: 'roomEnded',
        room_busy: 'roomFull',
      };
      const messages = {
        room_not_found: 'Room not found — check your link',
        room_ended: 'This call has ended',
        room_busy: 'This room is already full',
        no_offer: 'Room is not ready yet — try again in a moment',
      };
      const mode = errorModes[result.error] || 'default';
      update({ panelMode: mode });
      ui.renderPanelError(mode, messages[result.error] || `Failed to join: ${result.error}`);
      await rtc.teardown();
      reset();
      setStartCallLoading(false);
      return;
    }
  }
}

/**
 * Join a call by code or link entered in the join code box.
 * Parses the input, validates the slug, and transitions in-place to receiver mode.
 */
async function joinByCode() {
  const DOM = ui.getDOM();
  const raw = DOM.joinCodeInput?.value || '';
  const extracted = parseJoinInput(raw);

  if (!extracted) {
    showNotification('Enter a join code or link', 'error');
    DOM.joinCodeInput?.focus();
    return;
  }

  const result = normalizeSlug(extracted);
  if (!result.valid) {
    showNotification(result.error || 'Invalid join code', 'error');
    DOM.joinCodeInput?.focus();
    return;
  }

  // Clear custom link input so startCall() doesn't override to sender mode
  if (DOM.customLinkInput) DOM.customLinkInput.value = '';

  // Transition to receiver mode in-place
  update({ roomId: result.slug, role: Role.RECEIVER });

  // Update URL without reloading
  const newUrl = `${window.location.pathname}?session=${encodeURIComponent(result.slug)}`;
  window.history.pushState({ path: newUrl }, '', newUrl);

  // Configure panel for receiver and start call
  ui.configureConnectionPanel(Role.RECEIVER, buildSessionUrl(result.slug));
  startCall();
}

/**
 * End the current call.
 */
async function endCall(e) {
  if (e && !e.shiftKey) {
    const confirmed = await ui.showConfirmModal({
      title: 'End Call?',
      message: 'You will be disconnected from this session.',
      confirmLabel: 'End Call',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
  }

  _wasReconnecting = false;
  await rtc.teardown();
  ui.clearFileTransfers();
  reset();

  showNotification('Call ended', 'info');

  // Reset URL to clean base path (no stale ?session= on reload)
  window.history.replaceState({}, '', window.location.pathname);

  // Restore to fresh sender state for next call
  const newId = generateSessionId();
  update({ roomId: newId, role: Role.SENDER });
  ui.configureConnectionPanel(Role.SENDER, buildSessionUrl(newId));

  // Restart device preview for next call
  startDevicePreview();
}

/**
 * Handle remote track arriving.
 */
function handleRemoteTrack(_remoteStream) {
  const state = getMutableState();
  showNotification('Connected!', 'success');

  if (!state.hasAnnouncedJoin) {
    const peerName = state.remoteNickname || 'Peer';
    chat.addCommandMessage(`${peerName} has joined!`);
    update({ hasAnnouncedJoin: true });
  }
}

/**
 * Route incoming data channel messages.
 * File-related messages go to rtc.handleFileMessage; everything else to chat.
 */
function handleDataMessage(data) {
  if (data.type === 'file-start' || data.type === 'file-chunk' || data.type === 'file-end'
      || data.type === 'file-accept' || data.type === 'file-deny') {
    rtc.handleFileMessage(data);
  } else {
    chat.handlePeerMessage(data);
  }
}

/**
 * Handle connection state changes.
 */
let _wasReconnecting = false;

function handleConnectionStateChange(connState) {
  const state = getState();

  if (connState === 'failed') {
    if (state.phase === Phase.CONNECTED) {
      chat.addCommandMessage('Connection lost — peer may have left');
    }
  }
  if (connState === 'disconnected') {
    _wasReconnecting = true;
    chat.addCommandMessage('Reconnecting…');
  }
  if (connState === 'connected' && _wasReconnecting) {
    _wasReconnecting = false;
    chat.addCommandMessage('Reconnected!');
  }
}

/**
 * Handle data channel state changes.
 */
function handleDataChannelStateChange(dcState) {
  if (dcState === 'closed') {
    const state = getState();
    if (state.phase === Phase.CONNECTED || state.phase === Phase.RECONNECTING) {
      update({ phase: Phase.RECONNECTING });
      rtc.requestIceRestart();
    }
  }
}

/**
 * Handle connection quality updates from the RTC layer.
 */
function handleQualityUpdate(quality) {
  ui.renderQualityBadge(quality);
}

/**
 * Switch media devices.
 */
async function switchDevices(videoId, audioId) {
  const stream = await rtc.initializeMedia(videoId, audioId);
  if (stream) {
    const vizLevel = document.getElementById('audioVisualizerLevel');
    if (vizLevel) rtc.setupAudioVisualizer(vizLevel);
  }
}

/**
 * Get normalized slug from the custom link input.
 * Uses the shared normalizeSlug from state.js for consistent cleaning.
 */
function normalizeSlugFromInput() {
  const DOM = ui.getDOM();
  const raw = DOM.customLinkInput?.value || '';
  return normalizeSlug(raw).slug;
}

/**
 * Handle keyboard shortcuts.
 */
function handleKeyboardShortcut(e) {
  // Don't trigger shortcuts when typing in input fields
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

  const key = e.key.toLowerCase();
  const state = getMutableState();
  const mobile = isMobileDevice();
  const DOM = ui.getDOM();

  switch (key) {
    case 'escape':
      if (state.isFullscreen) {
        update({ isFullscreen: false });
      }
      if (state.isEmojiPickerOpen) {
        update({ isEmojiPickerOpen: false });
      }
      if (!mobile && state.isSplitView) {
        update({ isSplitView: false });
      }
      break;

    case 'm':
      if (state.localStream?.getAudioTracks().length > 0) {
        const newVal = !state.isAudioEnabled;
        update({ isAudioEnabled: newVal });
        state.localStream.getAudioTracks()[0].enabled = newVal;
      }
      break;

    case 'v':
      if (state.localStream?.getVideoTracks().length > 0) {
        const newVal = !state.isVideoEnabled;
        update({ isVideoEnabled: newVal });
        state.localStream.getVideoTracks()[0].enabled = newVal;
      }
      break;

    case 's':
      if (!mobile && state.phase === Phase.CONNECTED) {
        update({ isSplitView: !state.isSplitView });
      }
      break;

    case 'f':
      if (!state.isSplitView && state.phase === Phase.CONNECTED) {
        update({ isFullscreen: !state.isFullscreen });
      }
      break;

    case 'r':
      if (!mobile && state.phase === Phase.CONNECTED) {
        DOM.screenShareBtn?.click();
      }
      break;

    case 'p':
      if (!mobile && state.phase === Phase.CONNECTED) {
        DOM.pipBtn?.click();
      }
      break;

    case 't':
      if (state.phase === Phase.CONNECTED) {
        DOM.snapshotBtn?.click();
      }
      break;

    case 'e':
      if (state.phase !== Phase.IDLE && state.phase !== Phase.ENDED) {
        endCall({ shiftKey: e.shiftKey });
      }
      break;

    case 'c':
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const slug = normalizeSlugFromInput();
        const linkId = slug || state.roomId;
        copyToClipboard(buildSessionUrl(linkId)).then((ok) => {
          showNotification(ok ? 'Link copied!' : 'Failed to copy', ok ? 'success' : 'error');
        });
      }
      break;

    case 'd':
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        update({ isDarkMode: !state.isDarkMode });
      }
      break;

    case '?':
      if (e.shiftKey) {
        chat.addShortcutsMessage();
      }
      break;
  }
}

/**
 * Handle before-unload — best-effort departure.
 * Fires an async Firestore update to mark the room ended, but this may not
 * complete before the page unloads. The heartbeat TTL handles eventual staleness.
 */
function handleBeforeUnload(e) {
  const state = getState();
  // Stop preview stream if active
  if (state.previewStream) {
    rtc.stopPreview();
  }
  if (state.phase !== Phase.IDLE && state.phase !== Phase.ENDED) {
    // Warn user before closing during an active call
    if (state.phase === Phase.CONNECTED || state.phase === Phase.RECONNECTING) {
      e.preventDefault();
      e.returnValue = '';
      // Show a custom overlay behind the native dialog for visual consistency
      ui.showLeaveCallOverlay();
    }
    // Best-effort departure: mark room ended, unsub listeners
    signaling.depart(state.roomId);

    // Close peer connection synchronously
    if (state.peerConnection) {
      try {
        state.peerConnection.close();
      } catch {}
    }
    if (state.dataChannel) {
      try {
        state.dataChannel.close();
      } catch {}
    }
    if (state.localStream) {
      state.localStream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
    }
  }
}

/**
 * Load analytics script if enabled.
 */
function loadAnalytics() {
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = ANALYTICS.trackerUrl;
  script.dataset.apiUrl = ANALYTICS.apiUrl;
  document.body.appendChild(script);
}

// --- Boot on DOM ready ---
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
